import React from 'react';
import {
  UtensilsCrossed,
  Clock,
  Heart,
  Check,
  Coffee,
  Cake,
  Soup,
  Apple,
  Cookie,
  ChefHat,
} from 'lucide-react';

const foodFeatures = [
  'Freshly prepared meals cooked on-site',
  'Choice of menus tailored to preferences',
  'Special dietary requirements catered for',
  'Flexible mealtimes',
  'Snacks and drinks available throughout the day',
];

const dailySchedule = [
  {
    time: 'Breakfast',
    icon: Coffee,
    title: 'A relaxed start to the day',
    description:
      'Cereals, porridge, fresh fruit and toast plus a daily-changing choice of hot options. Served whenever residents are ready to start their morning.',
  },
  {
    time: 'Mid-Morning',
    icon: Cake,
    title: 'Coffee & cake',
    description:
      'A warm cup of tea or coffee with freshly baked cake; a chance to chat and catch up before lunch.',
  },
  {
    time: 'Lunch',
    icon: Soup,
    title: 'The main meal of the day',
    description:
      'A restaurant-style sit-down meal with hot dish choices, or a lighter option of soup or sandwich, finished with delicious desserts prepared on-site.',
  },
  {
    time: 'Afternoon Tea',
    icon: Cookie,
    title: 'A treat to share',
    description:
      'Mid-afternoon tea with mouth-watering home-baked cakes, enjoyed by residents and team members together as an activity in itself.',
  },
  {
    time: 'Evening',
    icon: Apple,
    title: 'Lighter suppers & snacks',
    description:
      'After supper, lighter meal options are offered, with fresh fruit and snacks available throughout the day to keep residents nourished and satisfied.',
  },
];

const FoodSection = () => {
  return (
    <section
      id="food"
      className="py-20 md:py-32 bg-white"
      data-testid="food-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Row */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a227]/10 rounded-full text-[#c9a227] text-sm font-medium mb-6">
              <UtensilsCrossed className="w-4 h-4" />
              Food & Dining
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6 leading-tight">
              Nutritious, Enjoyable Mealtimes
            </h2>

            <p className="text-slate-600 text-lg mb-6 leading-relaxed">
              Our chef teams use great ingredients to create freshly
              prepared, nutritionally balanced meals built around regularly
              changing seasonal menus. We make mealtimes a real occasion,
              served in welcoming surroundings and looked after by our
              attentive care teams.
            </p>

            <p className="text-slate-600 mb-8 leading-relaxed">
              Residents always have multiple options to choose from at every
              meal, and they are free to change their mind once the food
              arrives. Whatever the appetite or preference, we will find a
              dish that hits the spot.
            </p>

            {/* Features List */}
            <div className="space-y-4 mb-8">
              {foodFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-slate-700">{feature}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-xl p-6 text-center">
                <Clock className="w-8 h-8 text-[#c9a227] mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#1e3a5f]">3+</div>
                <div className="text-sm text-slate-500">Meals Daily</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-6 text-center">
                <Heart className="w-8 h-8 text-[#c9a227] mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#1e3a5f]">100%</div>
                <div className="text-sm text-slate-500">Fresh Ingredients</div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_github-sync-86/artifacts/efa5fa01_IMG_1314.jpeg"
                alt="Beautifully set dining tables at Abney Low Nursing Home"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -z-10 -bottom-4 -left-4 w-full h-full border-2 border-[#c9a227]/30 rounded-2xl" />
          </div>
        </div>

        {/* Daily Schedule */}
        <div className="mt-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1e3a5f] text-white rounded-full text-xs font-medium mb-4">
              <ChefHat className="w-3.5 h-3.5" />
              A Day at the Table
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-4">
              Mealtimes throughout the day
            </h3>
            <p className="text-slate-600">
              From a leisurely breakfast through to a lighter evening
              supper, every mealtime is an occasion to enjoy.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {dailySchedule.map((meal, index) => {
              const Icon = meal.icon;
              return (
                <div
                  key={meal.time}
                  className="bg-slate-50 rounded-2xl p-6 hover:bg-[#FDF6E3] hover:shadow-md transition-all border border-transparent hover:border-[#c9a227]/30"
                  data-testid={`meal-card-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#c9a227]" />
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#c9a227] mb-1">
                    {meal.time}
                  </div>
                  <h4 className="font-bold text-[#1e3a5f] mb-2 leading-snug">
                    {meal.title}
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {meal.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FoodSection;
